import { IBody } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnTypes, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'

import { BooleanCell } from '@/core/models/columns/boolean'
import { NumberCell } from '@/core/models/columns/number'
import { RatingCell } from '@/core/models/columns/rating'

export const FORM_VALIDATION_PATTERNS = [
  {
    id: 'none',
    pattern: 'none',
    label: 'N/A',
    error: '',
    helpText: '',
  },
  {
    id: 'alphabetic',
    pattern: '^[a-zA-Z]+$',
    label: 'Alphabetic',
    error: 'Please enter only alphabetic characters',
    helpText: 'Letters only',
  },
  {
    id: 'alphanumeric',
    pattern: '^[a-zA-Z0-9]+$',
    label: 'Alphanumeric',
    error: 'Please enter only alphanumeric characters',
    helpText: 'Letters and numbers only allowed',
  },
  {
    id: 'alphanumeric_space',
    pattern: '^[a-zA-Z0-9 ]+$',
    label: 'Alphanumeric With Space',
    error: 'Please enter only alphanumeric characters with space',
    helpText: 'Letters, numbers and spaces allowed',
  },
  {
    id: 'no_special_chars',
    pattern: '^[a-zA-Z0-9_\\- ]+$',
    label: 'No Special Characters',
    error: 'Please enter only letters, numbers, underscores, hyphens and spaces',
    helpText: 'No special characters allowed',
  },
  {
    id: 'email',
    pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
    label: 'Email',
    error: 'Please enter a valid email address',
    helpText: 'Valid email, ie: name@example.com',
  },
  {
    id: 'url',
    pattern: '^(?:https?:\\/\\/)?(?:[\\w-]+\\.)+[a-z]{2,}(?:[\\/\\w-]*)*(?:[?#]\\S*)?$',
    label: 'URL',
    error: 'Please enter a valid URL',
    helpText: 'Website address with or without https://',
  },
  {
    id: 'phone_basic',
    pattern: '^[0-9]{7,15}$',
    label: 'Phone Number Basic',
    error: 'Please enter a valid phone number',
    helpText: 'Numbers only',
  },
  {
    id: 'phone_us',
    pattern: '^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$',
    label: 'Phone Number US Format',
    error: 'Please enter a valid phone number',
    helpText: 'US phone number, ie: (555) 555-5555',
  },
  {
    id: 'phone_international',
    pattern: '^\\+?[0-9]{1,15}$',
    label: 'Phone Number International',
    error: 'Please enter a valid phone number',
    helpText: 'International number, ie: +12345678901',
  },
  {
    id: 'domain',
    pattern: '^([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*.)+[a-zA-Z]{2,}$',
    label: 'Domain Name',
    error: 'Please enter a valid domain name',
    helpText: 'Website domain, ie: example.com',
  },
  {
    id: 'integer',
    pattern: '^-?\\d+$',
    label: 'Integer',
    error: 'Please enter a valid integer',
    helpText: 'Whole number only',
  },
  {
    id: 'positive_integer',
    pattern: '^\\d+$',
    label: 'Positive Integer',
    error: 'Please enter a valid positive integer',
    helpText: 'Positive number only',
  },
  {
    id: 'negative_integer',
    pattern: '^-\\d+$',
    label: 'Negative Integer',
    error: 'Please enter a valid negative integer',
    helpText: 'Negative number only',
  },
  {
    id: 'decimal',
    pattern: '^-?\\d*\\.?\\d+$',
    label: 'Decimal',
    error: 'Please enter a valid decimal',
    helpText: 'Any number',
  },
  {
    id: 'positive_decimal',
    pattern: '^\\d*\\.?\\d+$',
    label: 'Positive Decimal',
    error: 'Please enter a valid positive decimal',
    helpText: 'Positive number only',
  },
  {
    id: 'negative_decimal',
    pattern: '^-\\d*\\.?\\d+$',
    label: 'Negative Decimal',
    error: 'Please enter a valid negative decimal',
    helpText: 'Negative numbers only',
  },
  {
    id: 'coordinate',
    pattern: '^(-?\\d+\\.?\\d*),\\s*(-?\\d+\\.?\\d*)$',
    label: 'Coordinate',
    error: 'Please enter a valid coordinate (latitude, longitude)',
    helpText: 'Valid coordinates, ie: 42.1234, -71.1234',
  },
]

export function validatePattern(pattern: string, value: string, callback: (isValid: boolean, error?: string) => void) {
  if (pattern === 'none') return callback(true)

  const patternObj = FORM_VALIDATION_PATTERNS.find((p) => p.pattern === pattern)
  if (!patternObj) return callback(true)

  const regexPattern = new RegExp(pattern)
  const isValid = regexPattern.test(value)
  if (isValid) return callback(true)

  const errorMessage = patternObj?.error ?? ''
  return callback(false, errorMessage)
}

export function getCurrentFormFieldColumns(containerSettings: Record<string, any>, formChildren: IBody[]) {
  const columnSet = new Set<string>()

  formChildren.forEach((el) => {
    const elSettings = containerSettings[el._id]

    if (elSettings) {
      Object.entries(elSettings).forEach(([key, value]) => {
        if (typeof value === 'string' && key.includes('-data-value')) {
          columnSet.add(value)
        }
      })
    }
  })

  return Array.from(columnSet)
}
/**
 * Format column value for form state.
 * only for form field components.
 *
 * ?info: this is used to parse interanl value, not for display!
 */
export function formatColumnValue(value?: any, column?: ITableColumn) {
  if (value === undefined || value === null || !column) return null

  switch (column._Type) {
    case ColumnTypes.Number:
      return new NumberCell(value, column._Settings as any).value
    case ColumnTypes.Rating:
      return new RatingCell(value, column._Settings as any).value
    case ColumnTypes.Boolean:
      return new BooleanCell(value, column._Settings as any).value
    default:
      return value.toString()
  }
}
